import { useLayoutEffect, useRef } from 'react'

/**
 * Publish an element's MEASURED height as a CSS custom property on `:root`.
 *
 * WHY THIS EXISTS, 2026-09-13. Three bars stack down the top of a module page — the app header, the
 * module nav under it, and on the bid editor a title bar under that. The lower two each carried a
 * hardcoded offset (`top-[92px]` in nine navs, `top-[144px]` in the bid editor) guessing a height
 * they could not see. The app header is not 92px tall: `.ah-root` is padded in rem, `.ah-logo` is
 * 38px on a desktop and 30px under 768px, `.ah-inner` gains `flex-wrap: wrap` at that same
 * breakpoint, and `.ah-nav-strip` is a whole second row. The number is right at one viewport width
 * and wrong everywhere else, which is what the owner reported: a strip of unowned space under the
 * nav with the page scrolling through it.
 *
 * Two rules make this safe to layer:
 *
 *   - A height of zero is NEVER published. In jsdom, and in the frame before layout, every element
 *     measures zero; writing `0px` would collapse the stack onto itself. The declared fallback in
 *     `styles/index.css` stands instead.
 *   - Cleanup removes the property only if the value still standing is the one WE wrote. Route
 *     swaps unmount the outgoing nav around the incoming one's effects, and a blind
 *     `removeProperty` there would wipe a live measurement and leave the page on its fallback.
 */
export default function usePublishedHeight(varName) {
  const ref = useRef(null)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return undefined

    const root = document.documentElement
    let mine = null

    const write = () => {
      const h = Math.round(el.getBoundingClientRect().height)
      if (h <= 0) return
      const next = `${h}px`
      if (next === mine) return
      mine = next
      root.style.setProperty(varName, next)
    }

    write()

    let ro
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(write)
      ro.observe(el)
    }
    // A ResizeObserver covers the element changing shape. Orientation and zoom can change what the
    // element is measured AGAINST without changing the element, so listen for both.
    window.addEventListener('resize', write)

    return () => {
      if (ro) ro.disconnect()
      window.removeEventListener('resize', write)
      if (mine !== null && root.style.getPropertyValue(varName) === mine) {
        root.style.removeProperty(varName)
      }
    }
  }, [varName])

  return ref
}
